import { Box } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { MinSessionDurationFilter } from "../../../components";
import { parseDimension } from "../../../domain/mappers/metricsMappers";
import type { Dimension } from "../../../domain/models/metrics";
import { useSessionStorageState } from "../../../hooks/useSessionStorageState";
import { useUsers } from "../../users/hooks/useUsers";
import { StickyToolbarWrapper } from "../InternalUsageTab/components/StickyToolbarWrapper";
import {
  DEFAULT_DIMENSION,
  DEFAULT_END_DATE,
  DEFAULT_START_DATE,
  DIMENSION_KEY,
  EARLIEST_DATA_DATE,
  END_DATE_KEY,
  MIN_SESSION_DURATION_KEY,
  SHOWING_OPTION_KEY,
  START_DATE_KEY,
} from "../InternalUsageTab/constants";
import { parseMinSessionDuration } from "../InternalUsageTab/utils";
import { normalizeMetricDateRange } from "../utils/metricDateRange";
import { aggregateUserUsage } from "./aggregateUserUsage";
import { SeatHistoryTable } from "./SeatHistoryTable";
import { SessionHistoryToolbar } from "./SessionHistoryToolbar";
import { ShowingOption, type UserUsageEntry } from "./types";
import { UserSessionTable } from "./UserSessionTable";
import { UserUsageTable } from "./UserUsageTable";
import { useSeatHistory } from "./useSeatHistory";
import { useUserSessionHistory } from "./useUserSessionHistory";

export interface UserLevelDataTabProps {
  hasTabs?: boolean;
}

export const UserLevelDataTab: React.FC<UserLevelDataTabProps> = ({
  hasTabs = true,
}) => {
  const { companyId } = useParams();
  const [startDate, setStartDate] = useSessionStorageState<Date>(
    START_DATE_KEY,
    DEFAULT_START_DATE,
  );
  const [endDate, setEndDate] = useSessionStorageState<Date>(
    END_DATE_KEY,
    DEFAULT_END_DATE,
  );
  const [showingOption, setShowingOption] =
    useSessionStorageState<ShowingOption>(
      SHOWING_OPTION_KEY,
      ShowingOption.USER_SESSIONS,
    );

  const [dimension, setDimension] = useSessionStorageState<Dimension>(
    DIMENSION_KEY,
    DEFAULT_DIMENSION,
    parseDimension,
  );

  const [minSessionDuration, setMinSessionDuration] = useSessionStorageState<
    number | null
  >(MIN_SESSION_DURATION_KEY, null, parseMinSessionDuration);

  const { users, isLoading: isUsersLoading } = useUsers({
    companyId,
    enabled: !!companyId,
  });

  const userDisplayNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) {
      map.set(u.userId, `${u.forename} ${u.surname}`.trim());
    }

    return map;
  }, [users]);

  const userEmails = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) {
      map.set(u.userId, u.emailAddress);
    }

    return map;
  }, [users]);

  const [filteredSessionData, setFilteredSessionData] = useState<
    ReturnType<typeof useUserSessionHistory>["data"] | undefined
  >(undefined);
  const [filteredSeatIds, setFilteredSeatIds] = useState<
    Set<string> | undefined
  >(undefined);
  const [filteredUserUsageData, setFilteredUserUsageData] = useState<
    UserUsageEntry[] | undefined
  >(undefined);

  const handleSessionFilteredDataChange = useCallback(
    (rows: ReturnType<typeof useUserSessionHistory>["data"]) =>
      setFilteredSessionData(rows),
    [],
  );
  const handleUserUsageFilteredDataChange = useCallback(
    (rows: UserUsageEntry[]) => setFilteredUserUsageData(rows),
    [],
  );
  const handleSeatFilteredDataChange = useCallback(
    (rows: ReturnType<typeof useSeatHistory>["data"]) =>
      setFilteredSeatIds(new Set(rows.map((r) => r.id))),
    [],
  );

  const effectiveStartDate = useMemo(
    () => (startDate < EARLIEST_DATA_DATE ? EARLIEST_DATA_DATE : startDate),
    [startDate],
  );

  const { startDate: normalizedStart, endDate: normalizedEnd } = useMemo(
    () => normalizeMetricDateRange(effectiveStartDate, endDate),
    [effectiveStartDate, endDate],
  );

  const historiesEnabled = Boolean(companyId);

  const {
    data: sessionData,
    isLoading: isSessionsLoading,
    isFetching: isSessionsFetching,
  } = useUserSessionHistory({
    startDate: normalizedStart,
    endDate: normalizedEnd,
    minSessionDurationInMinutes: minSessionDuration ?? undefined,
    enabled: historiesEnabled,
  });

  const {
    data: seatData,
    isLoading: isSeatLoading,
    isFetching: isSeatFetching,
  } = useSeatHistory({
    startDate: normalizedStart,
    endDate: normalizedEnd,
    enabled: historiesEnabled,
  });

  const userUsageData = useMemo(
    () => aggregateUserUsage(sessionData),
    [sessionData],
  );

  const filteredSeatData = useMemo(
    () =>
      filteredSeatIds
        ? seatData.filter((e) => filteredSeatIds.has(e.id))
        : undefined,
    [filteredSeatIds, seatData],
  );

  const minSessionDurationFilter = (
    <MinSessionDurationFilter
      onChange={setMinSessionDuration}
      value={minSessionDuration}
    />
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <StickyToolbarWrapper hasTabs={hasTabs}>
        <SessionHistoryToolbar
          data={filteredSessionData ?? sessionData}
          dimension={dimension}
          endDate={normalizedEnd}
          isLoading={
            showingOption === ShowingOption.SEAT_HISTORY
              ? isSeatFetching
              : isSessionsFetching
          }
          onDimensionChange={setDimension}
          onEndDateChange={setEndDate}
          onShowingOptionChange={setShowingOption}
          onStartDateChange={setStartDate}
          seatHistoryData={filteredSeatData ?? seatData}
          showingOption={showingOption}
          startDate={normalizedStart}
          userDisplayNames={userDisplayNames}
          userEmails={userEmails}
          userUsageData={filteredUserUsageData ?? userUsageData}
        />
      </StickyToolbarWrapper>

      <Box
        data-testid="seat-history-view"
        sx={{
          display:
            showingOption === ShowingOption.SEAT_HISTORY ? "block" : "none",
        }}
      >
        <SeatHistoryTable
          data={seatData}
          isLoading={isSeatLoading}
          onFilteredDataChange={handleSeatFilteredDataChange}
        />
      </Box>

      <Box
        data-testid="user-sessions-view"
        sx={{
          display:
            showingOption === ShowingOption.USER_SESSIONS ? "block" : "none",
        }}
      >
        <UserSessionTable
          data={isUsersLoading ? [] : sessionData}
          filterSlot={
            showingOption === ShowingOption.USER_SESSIONS
              ? minSessionDurationFilter
              : undefined
          }
          isLoading={isSessionsLoading || isUsersLoading}
          onFilteredDataChange={handleSessionFilteredDataChange}
          userDisplayNames={userDisplayNames}
        />
      </Box>

      <Box
        data-testid="user-usage-view"
        sx={{
          display:
            showingOption === ShowingOption.USER_USAGE ? "block" : "none",
        }}
      >
        <UserUsageTable
          data={isUsersLoading ? [] : userUsageData}
          filterSlot={
            showingOption === ShowingOption.USER_USAGE
              ? minSessionDurationFilter
              : undefined
          }
          isLoading={isSessionsLoading || isUsersLoading}
          onFilteredDataChange={handleUserUsageFilteredDataChange}
          userDisplayNames={userDisplayNames}
          userEmails={userEmails}
        />
      </Box>
    </Box>
  );
};
